import { useState, useRef, useEffect, useContext } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
  Spinner,
  useColorModeValue,
  Box,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  ButtonGroup,
  InputGroup,
  InputLeftElement,
  Icon,
  Tooltip,
} from '@chakra-ui/react';

import { FaUser } from 'react-icons/fa';

import { apiLeadStatusUpdate, apiGetLeadHandlers } from 'api';

import { AuthContext } from 'contexts/AuthContext';

import SelectSearch from 'components/fields/SelectSearch';

import {
  LEAD_EDIT_STATUSES,
  LOST_REASONS,
  LEAD_STATUS_LOST,
  LEAD_STATUS_APPLICATION_IN_PROGRESS,
} from 'views/leads/constants';

import type {
  APILeadsListResultData,
  APIStatusChangeData,
  APILeadOverviewData,
  APIUserData,
} from 'types/api.types';

const LeadStatusChange = ({
  lead,
  onSuccess,
  leadStatues,
  showProcessedBy = false,
}: {
  lead: APILeadsListResultData | APILeadOverviewData;
  onSuccess?: (data: APIStatusChangeData) => void;
  leadStatues?: { value: string; label: string }[];
  showProcessedBy?: boolean;
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAppInProgressDialogOpen, setIsAppInProgressDialogOpen] =
    useState(false);
  const [selectedStatus, setSelectedStatus] = useState(lead.status);
  const [lostReason, setLostReason] = useState(null);
  const [selectedHandler, setSelectedHandler] = useState<number | null>(null);
  const [isHandlerSelectionOpen, setIsHandlerSelectionOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<
    'submitLater' | 'submitNow' | 'assignHandler' | null
  >(null);

  const cancelRef = useRef<HTMLButtonElement>(null);

  // Fetch handlers when handler selection is open
  const { data: handlers, isLoading: isLoadingHandlers } = useQuery<
    APIUserData[]
  >({
    queryKey: ['leadHandlers', isHandlerSelectionOpen],
    queryFn: () => apiGetLeadHandlers(),
    enabled: isHandlerSelectionOpen,
  });

  const mutation = useMutation({
    mutationFn: (data: {
      id: number;
      status: string;
      lost_reason?: string;
      handler?: number;
    }) => {
      const payload: {
        status: string;
        lost_reason?: string;
        handler?: number;
      } = {
        status: data.status,
      };

      if (data.lost_reason) {
        payload.lost_reason = data.lost_reason;
      }

      if (data.handler !== undefined) {
        payload.handler = data.handler;
      }

      return apiLeadStatusUpdate(data.id, payload);
    },
    onSuccess: (data: APIStatusChangeData, variables) => {
      toast({
        title: `Status updated to ${data.status_display}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      if (activeAction === 'submitNow') {
        navigate(
          `/admin/leads/${variables.id}?openSubmission=true#submissions`,
        );
      }

      setActiveAction(null);

      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      toast({
        title: 'Error updating status',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setActiveAction(null);
      setSelectedStatus(lead.status);
    },
  });

  const onStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = event.target.value;

    if (newStatus === LEAD_STATUS_LOST.toString()) {
      setSelectedStatus(
        newStatus as unknown as APILeadsListResultData['status'],
      );
      setIsDialogOpen(true);
    } else if (newStatus === LEAD_STATUS_APPLICATION_IN_PROGRESS.toString()) {
      setSelectedStatus(
        newStatus as unknown as APILeadsListResultData['status'],
      );
      setIsAppInProgressDialogOpen(true);
    } else {
      setSelectedStatus(
        newStatus as unknown as APILeadsListResultData['status'],
      );
      mutation.mutate({
        id: lead.id,
        status: newStatus.toString(),
      });
    }
  };

  const handleLostReasonChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setLostReason(event.target.value);
  };

  const confirmLostStatus = () => {
    mutation.mutate({
      id: lead.id,
      status: LEAD_STATUS_LOST.toString(),
      lost_reason: lostReason.toString(),
    });
  };

  const cancelLostStatus = () => {
    setSelectedStatus(lead.status);
    setIsDialogOpen(false);
  };

  const handleSubmitLater = () => {
    setActiveAction('submitLater');
    mutation.mutate({
      id: lead.id,
      status: LEAD_STATUS_APPLICATION_IN_PROGRESS.toString(),
      handler: user.id,
    });
    setIsAppInProgressDialogOpen(false);
  };

  const handleSubmitNow = () => {
    setActiveAction('submitNow');
    setIsAppInProgressDialogOpen(false);
    setIsDialogOpen(false);

    mutation.mutate({
      id: lead.id,
      status: LEAD_STATUS_APPLICATION_IN_PROGRESS.toString(),
      handler: user.id,
    });
  };

  const handleAssignUser = () => {
    setIsHandlerSelectionOpen(true);
  };

  const confirmHandlerAssignment = () => {
    if (selectedHandler) {
      setActiveAction('assignHandler');
      mutation.mutate({
        id: lead.id,
        status: LEAD_STATUS_APPLICATION_IN_PROGRESS.toString(),
        handler: selectedHandler,
      });
      setIsHandlerSelectionOpen(false);
      setIsAppInProgressDialogOpen(false);
    }
  };

  const cancelAppInProgressDialog = () => {
    setSelectedStatus(lead.status);
    setIsAppInProgressDialogOpen(false);
    setIsHandlerSelectionOpen(false);
    setSelectedHandler(null);
  };

  useEffect(() => {
    setSelectedStatus(lead.status);
  }, [lead.status]);

  return (
    <>
      {!mutation.isPending && !mutation.isError && (
        <Box color={textColor} w="100%">
          <InputGroup>
            {showProcessedBy && lead.handler_name && (
              <Tooltip
                label={`Submission Processed By ${lead.handler_name}`}
                placement="top"
              >
                <InputLeftElement>
                  <Icon as={FaUser} color="gray.500" />
                </InputLeftElement>
              </Tooltip>
            )}
            <Select
              variant="main"
              value={selectedStatus}
              onChange={onStatusChange}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
              pl={showProcessedBy && lead.handler_name ? '40px' : undefined}
            >
              {(leadStatues || LEAD_EDIT_STATUSES).map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </Select>
          </InputGroup>
        </Box>
      )}

      {mutation.isError && (
        <Box w="100%" textAlign="center">
          Error
        </Box>
      )}

      {mutation.isPending && <Spinner ml="auto" mr="auto" mt={2} mb={2} />}

      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelLostStatus}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Lost Lead
            </AlertDialogHeader>

            <AlertDialogBody>
              <FormControl>
                <FormLabel>
                  Please select a reason for losing this lead:
                </FormLabel>
                <Select
                  variant="main"
                  value={lostReason}
                  onChange={handleLostReasonChange}
                  placeholder="Select a reason"
                  autoFocus
                >
                  {LOST_REASONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelLostStatus}>
                Cancel
              </Button>
              <Button
                variant="brand"
                onClick={confirmLostStatus}
                ml={3}
                isDisabled={!lostReason || mutation.isPending}
                isLoading={mutation.isPending && isDialogOpen}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isAppInProgressDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelAppInProgressDialog}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Application In Progress
            </AlertDialogHeader>

            <AlertDialogBody>
              {!isHandlerSelectionOpen ? (
                <VStack spacing={4} align="stretch">
                  <ButtonGroup spacing={3} justifyContent="center" width="100%">
                    <Button
                      onClick={handleSubmitLater}
                      variant="brand"
                      isDisabled={mutation.isPending}
                      isLoading={
                        mutation.isPending && activeAction === 'submitLater'
                      }
                      w="100%"
                    >
                      Submit Later
                    </Button>
                    <Button
                      onClick={handleAssignUser}
                      variant="brand"
                      isDisabled={mutation.isPending}
                      w="100%"
                    >
                      Assign TSR/CSR
                    </Button>
                    <Button
                      onClick={handleSubmitNow}
                      variant="brand"
                      isDisabled={mutation.isPending}
                      isLoading={
                        mutation.isPending && activeAction === 'submitNow'
                      }
                      w="100%"
                    >
                      Submit Now
                    </Button>
                  </ButtonGroup>
                </VStack>
              ) : (
                <FormControl>
                  <FormLabel>Select submitter:</FormLabel>
                  <SelectSearch
                    value={selectedHandler || ''}
                    options={
                      handlers?.map((handler) => ({
                        value: handler.id,
                        label: `${handler.first_name} ${handler.last_name}`,
                      })) || []
                    }
                    onChange={(value) => setSelectedHandler(Number(value))}
                    isLoading={isLoadingHandlers}
                  />
                </FormControl>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelAppInProgressDialog}>
                Cancel
              </Button>
              {isHandlerSelectionOpen && (
                <Button
                  colorScheme="blue"
                  onClick={confirmHandlerAssignment}
                  ml={3}
                  isDisabled={!selectedHandler || mutation.isPending}
                  isLoading={
                    mutation.isPending && activeAction === 'assignHandler'
                  }
                >
                  Assign
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default LeadStatusChange;
